import type { Dictionary } from "../types";

/**
 * English is the reference locale and the fallback for any missing string.
 * Every locale is typed as `Dictionary`, so a missing or misspelled key is a
 * compile error rather than a string that silently disappears at runtime.
 */
export const en: Dictionary = {
  common: {
    appName: "Retrospecter",
    poweredBy: "Powered by",
    loading: "Loading…",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving…",
    returnHome: "Return Home",
    yours: "Yours",
    by: "by {name}",
  },

  nav: {
    logOut: "Log out",
    switchToDark: "Switch to dark theme",
    switchToLight: "Switch to light theme",
    darkTheme: "Dark theme",
    lightTheme: "Light theme",
    language: "Language",
  },

  login: {
    title: "Sign in",
    subtitle: "Staff access to Retrospecter retrospectives",
    redirectedNotice:
      "This retrospective is only available to staff members. Please sign in to continue.",
    username: "Email or Username",
    usernamePlaceholder: "name@company.com",
    password: "Password",
    submit: "Sign In",
    submitting: "Signing in…",
    invalidCredentials: "Invalid username or password.",
    providerUnavailable:
      "The authentication service is currently unavailable. Please try again shortly.",
    anonymousHint:
      "Retrospectives in progress are open to anyone with the link — no sign in needed.",
  },

  home: {
    title: "Retrospectives",
    subtitle: "Start a new session, or reopen one your team has already run.",
    newRetro: "New retrospective",
    nameLabel: "Retrospective Name",
    namePlaceholder: "e.g. Sprint 42 Retro",
    create: "Create Retrospective",
    creating: "Creating…",
    createError: "Could not create the retrospective.",
    draftHint:
      "New retrospectives start as a private draft. Activate one to share the link with your team.",
  },

  create: {
    title: "New retrospective",
    subtitle: "Name the session, then pick the format your team will work in.",
    back: "Back to retrospectives",
    chooseTemplate: "Choose a format",
    chooseHint: "Each preview shows the columns your team will see.",
    selected: "Selected",
    columnCount: { one: "{count} column", other: "{count} columns" },
    loadError: "Could not load the templates. Please refresh to try again.",
    error: "Could not create the retrospective.",
  },

  list: {
    heading: "Your boards",
    regionLabel: "Retrospectives",
    filterLabel: "Filter retrospectives",
    filterAll: "All",
    filterPast: "Past",
    filterRunning: "Running",
    filterDrafts: "Drafts",
    empty: "No retrospectives yet — create your first one above.",
    emptyFiltered: "Nothing here yet.",
    loadError: "Could not load retrospectives. Please refresh to try again.",
    createdOn: "Created {date}",
    completedOn: "Completed {date}",
    notes: { one: "{count} note", other: "{count} notes" },
    participants: { one: "{count} participant", other: "{count} participants" },
  },

  board: {
    loading: "Loading retrospective…",
    accessDeniedTitle: "Access Denied",
    accessDeniedBody:
      "You're signed in, but you don't have permission to view this retrospective.",
    notFoundTitle: "Not Found",
    notFoundBody: "The retrospective you're looking for doesn't exist.",
    activate: "Activate",
    activating: "Activating…",
    complete: "Complete",
    completing: "Completing…",
    joinPrompt: "Join to add notes, vote and rearrange the board.",
    join: "Join Retrospective",
    joining: "Joining…",
    joinError: "Could not join this retrospective.",
    draftNotice:
      "This retrospective is still a draft — only you can see it. Activate it to share the link with your team.",
    dragHint:
      "Tip: drag post-its anywhere, pull a corner to resize, and rest one on another to group them.",
    readOnly: "This retrospective has been completed and is now read-only",
    participantsRegion: "Participants",
    participantsHeading: "Participants ({count})",
    noParticipants: "Nobody has joined yet.",
    statusDraft: "DRAFT",
    statusActive: "ACTIVE",
    statusCompleted: "COMPLETED",
  },

  column: {
    empty: "Nothing here yet — drop a note anywhere",
    pileLabel: "Take a note from the pile",
    pileHint: "Drag a note onto a column — or press Enter to pick a column.",
    pileChoose: "Add a note to {column}",
    dropHere: "Drop to add a note",
    addNote: "Add note",
    addNoteTo: "Add note to {column}",
    newNoteFor: "New note for {column}",
    notePlaceholder: "Share your thoughts…",
    saveNote: "Save note",
    adding: "Adding…",
    cancelNote: "Cancel note",
  },

  note: {
    yourNote: "You",
    react: "React with {emoji}",
    unreact: "Remove your {emoji} reaction",
    addReaction: "Add a reaction",
    edit: "Edit",
    editNote: "Edit note",
    delete: "Delete",
    deleteNote: "Delete note",
    vote: "Vote for this note",
    unvote: "Remove your vote",
    voteHint: "Join the retrospective to vote",
    move:
      "Move note. Arrow keys nudge it around the column; hold Shift to send it to the next column.",
    moveHint: "Drag, or focus and use arrow keys",
    resize: "Resize note. Use arrow keys to change its size.",
    resizeHint: "Drag the corner, or focus and use arrow keys",
    draggable: "Draggable note",
    saveError: "Could not save the change.",
    deleteError: "Could not delete the note.",
    voteError: "Could not register your vote.",
    movedToColumn: "Moved to {column}",
    movedTo: "Moved to {x} across, {y} down in {column}",
    groupWithPrevious: "Group with the note above",
    ungroup: "Take out of the group",
    ungroupError: "Could not ungroup the note.",
    groupError: "Could not group the notes.",
    dropToGroup: "Drop to group",
    groupSummary: "{votes} votes across the group",
    groupedNotes: { one: "{count} note", other: "{count} notes" },
  },

  actions: {
    heading: "Action items",
    regionLabel: "Action items",
    intro: "What the team agreed to actually do.",
    empty: "Nothing agreed yet.",
    textLabel: "What are we going to do?",
    textPlaceholder: "Split the deploy pipeline in two",
    ownerLabel: "Who owns it?",
    ownerPlaceholder: "Name (optional)",
    unassigned: "Unassigned",
    add: "Add",
    adding: "Adding…",
    markDone: "Mark as done",
    markNotDone: "Mark as not done",
    delete: "Remove action item",
    saveError: "Could not save the action item.",
    carriedHeading: "From last time",
    carriedIntro: "Still open from the previous retrospective.",
    carriedFrom: "From {name}",
    carryOver: "Carry over",
    carriedCount: { one: "{count} open item", other: "{count} open items" },
  },

  phase: {
    label: "Phase",
    brainstorm: "Brainstorm",
    grouping: "Grouping",
    voting: "Voting",
    discussion: "Discussion",
    error: "Could not change the phase.",
  },

  timer: {
    label: "Timer",
    addMinutes: "+{count} min",
    minutes: "{count} min",
    stop: "Stop",
    finished: "Time's up",
  },

  notFound: {
    title: "Page Not Found",
    body: "Oops! The page you're looking for doesn't exist. Let's get you back on track.",
  },

  templates: {
    "Start/Stop/Continue": {
      name: "Start/Stop/Continue",
      description: "The default. Concrete changes to make, drop and keep.",
      columns: { Start: "Start", Stop: "Stop", Continue: "Continue" },
      hints: {
        "Start": "Something we're not doing yet that would help.",
        "Stop": "Something costing us time or morale that we should drop.",
        "Continue": "What's already working — name it so we keep it.",
      },
    },
    "Mad/Sad/Glad": {
      name: "Mad/Sad/Glad",
      description: "Surfaces how the sprint felt before debating what to do.",
      columns: { Mad: "Mad", Sad: "Sad", Glad: "Glad" },
      hints: {
        "Mad": "What frustrated or angered you this sprint.",
        "Sad": "What disappointed you or fell short of your hopes.",
        "Glad": "What pleased you or went better than expected.",
      },
    },
    "What Went Well": {
      name: "What Went Well",
      description: "A plain review that ends in commitments.",
      columns: {
        "What went well": "What went well",
        "What didn't": "What didn't",
        "Action items": "Action items",
      },
      hints: {
        "What went well": "Wins worth repeating, however small.",
        "What didn't": "Where we struggled — describe the problem, not the person.",
        "Action items": "A concrete next step, ideally with an owner.",
      },
    },
    "Rose/Bud/Thorn": {
      name: "Rose/Bud/Thorn",
      description: "Balances wins, problems and early promise.",
      columns: { Rose: "Rose", Bud: "Bud", Thorn: "Thorn" },
      hints: {
        "Rose": "A clear success or highlight worth celebrating.",
        "Bud": "An early idea or opportunity worth nurturing.",
        "Thorn": "A problem or blocker that hurt this sprint.",
      },
    },
    "4 Ls": {
      name: "4 Ls",
      description: "Separates feelings from learning. Good after a rough sprint.",
      columns: {
        Liked: "Liked",
        Learned: "Learned",
        Lacked: "Lacked",
        "Longed for": "Longed for",
      },
      hints: {
        "Liked": "What you enjoyed about how we worked.",
        "Learned": "New knowledge or insight you gained.",
        "Lacked": "What was missing — tools, time, clarity, support.",
        "Longed for": "What you wished we had but didn't.",
      },
    },
    Sailboat: {
      name: "Sailboat",
      description:
        "Visual format: what pushes the team along and what holds it back.",
      columns: {
        "Wind (helps us)": "Wind (helps us)",
        "Anchors (slows us)": "Anchors (slows us)",
        "Rocks (risks)": "Rocks (risks)",
        "Island (goal)": "Island (goal)",
      },
      hints: {
        "Wind (helps us)": "What pushed us forward and sped us up.",
        "Anchors (slows us)": "What dragged on us and slowed us down.",
        "Rocks (risks)": "Hazards ahead that could hurt us if ignored.",
        "Island (goal)": "What we're actually trying to reach.",
      },
    },
    KALM: {
      name: "KALM",
      description: "Sorts practices by whether to keep, add, do less or do more.",
      columns: {
        Keep: "Keep",
        Add: "Add",
        "Less of": "Less of",
        "More of": "More of",
      },
      hints: {
        "Keep": "Working as it is — protect it from change.",
        "Add": "Something new that's worth trying next.",
        "Less of": "Worth doing, but we're overdoing it.",
        "More of": "Working well — we'd benefit from more of it.",
      },
    },
    Starfish: {
      name: "Starfish",
      description: "Finer-grained than Start/Stop/Continue; good for mature teams.",
      columns: {
        "Keep doing": "Keep doing",
        "More of": "More of",
        "Less of": "Less of",
        "Start doing": "Start doing",
        "Stop doing": "Stop doing",
      },
      hints: {
        "Keep doing": "Effective exactly as it is — don't change it.",
        "More of": "Helping already; we'd gain from doing it more.",
        "Less of": "Not harmful, but there's too much of it.",
        "Start doing": "A new practice we should try.",
        "Stop doing": "Adding no value, or actively getting in the way.",
      },
    },
  },
};
